import React, { useState, useEffect } from 'react';
import { Users, DollarSign, FileText, TrendingUp, Trophy, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate, formatDateForInput } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import * as XLSX from 'xlsx';

const CustomerSummaryComponent = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [monthlyCustomerData, setMonthlyCustomerData] = useState(null);
  const [error, setError] = useState('');
  const [customerNames, setCustomerNames] = useState({});
  const [customerPhones, setCustomerPhones] = useState({});
  const [customerCredits, setCustomerCredits] = useState({});
  const [customerRemainingCredits, setCustomerRemainingCredits] = useState({});
  const [customerDistricts, setCustomerDistricts] = useState({});
  const [customerProvinces, setCustomerProvinces] = useState({});
  const [loadingNames, setLoadingNames] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [customerDataReady, setCustomerDataReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const initializeDateRange = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setStartDate(formatDate(sevenDaysAgo));
    setEndDate(formatDate(today));
  };

  // Get minimum allowed date (1 November 2025)
  const getMinAllowedDate = () => {
    return '2025-11-01';
  };

  // Get maximum allowed date (today)
  const getMaxAllowedDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    initializeDateRange();
  }, []);

  const getDateRange = () => {
    return {
      start_date: startDate,
      end_date: endDate
    };
  };

  const fetchCustomerAnalytics = async () => {
    setLoading(true);
    setError('');
    setCustomerData(null);
    setCustomerDataReady(false); // Reset when starting new data fetch

    try {
      // Validate date range
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const today = new Date();
      const minDate = new Date('2025-11-01');

      if (startDateObj < minDate) {
        setError('วันที่เริ่มต้นต้องไม่ก่อนวันที่ 1 พฤศจิกายน 2025');
        setLoading(false);
        return;
      }

      if (endDateObj > today) {
        setError('วันที่สิ้นสุดต้องไม่เกินวันปัจจุบัน');
        setLoading(false);
        return;
      }

      if (startDateObj > endDateObj) {
        setError('วันที่เริ่มต้นต้องมาก่อนวันที่สิ้นสุด');
        setLoading(false);
        return;
      }

      const dateRange = getDateRange();
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          group_by: "month"
        },
        filters: {
          company_code: "045"
        }
      };

      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/customer-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status && data.data) {
        setCustomerData(data.data);
        setCurrentPage(1); // Reset to first page when new data is loaded
        // Fetch customer names after getting the data and wait for completion
        if (data.data.customers) {
          await fetchAllCustomerNames(data.data.customers);
          // Skip monthly breakdown API call since it returns 404
        }
      } else {
        setError('ไม่พบข้อมูลลูกค้าในช่วงเวลาที่เลือก');
      }
    } catch (err) {
      console.error('Error fetching customer analytics:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyCustomerData = async () => {
    try {
      const dateRange = getDateRange();
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          group_by: "month"
        },
        filters: {
          company_code: "045"
        },
        details: true // Request detailed breakdown
      };

      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/customer-monthly-breakdown', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status && data.data) {
          setMonthlyCustomerData(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching monthly customer data:', err);
      // If API doesn't exist, we'll create the data from invoice details
      await fetchMonthlyCustomerDataFromInvoices();
    }
  };

  const fetchInvoiceDetailsForExcel = async () => {
    try {
      const dateRange = getDateRange();
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date
        },
        filters: {
          company_code: "045"
        },
        details: true
      };

      console.log('📋 Fetching invoice details with params:', requestBody);

      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/invoice-details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status && data.data && data.data.invoices) {
          console.log(`✅ Successfully fetched ${data.data.invoices.length} invoices for Excel`);
          console.log('📊 Sample invoice:', data.data.invoices[0]);
          return data.data.invoices;
        } else {
          console.log('❌ Invoice details API response invalid:', data);
        }
      } else {
        console.log('❌ Invoice details API request failed:', response.status);
      }
    } catch (err) {
      console.error('Error fetching invoice details for Excel:', err);
    }
    
    return null;
  };

  const fetchMonthlyCustomerDataForExcel = async () => {
    try {
      const dateRange = getDateRange();
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          group_by: "month"
        },
        filters: {
          company_code: "045"
        },
        details: true // Request detailed breakdown
      };

      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/customer-monthly-breakdown', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status && data.data) {
          console.log('✅ Successfully fetched data from customer-monthly-breakdown API:', data.data);
          return data.data;
        } else {
          console.log('❌ customer-monthly-breakdown API response invalid:', data);
        }
      } else {
        console.log('❌ customer-monthly-breakdown API request failed:', response.status);
      }
    } catch (err) {
      console.error('Error fetching monthly customer data for Excel:', err);
    }
    
    // Fallback to invoice data processing
    console.log('📋 Falling back to invoice data processing...');
    return await fetchMonthlyCustomerDataFromInvoicesForExcel();
  };

  const fetchMonthlyCustomerDataFromInvoicesForExcel = async () => {
    try {
      const dateRange = getDateRange();
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date
        },
        filters: {
          company_code: "045"
        },
        details: true
      };

      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/invoice-details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status && data.data && data.data.invoices) {
          console.log(`📊 Processing ${data.data.invoices.length} invoices for monthly breakdown`);
          const processedData = processInvoicesForMonthlyBreakdown(data.data.invoices);
          console.log('🔄 Processed monthly data:', processedData);
          return processedData;
        } else {
          console.log('❌ invoice-details API response invalid:', data);
        }
      } else {
        console.log('❌ invoice-details API request failed:', response.status);
      }
    } catch (err) {
      console.error('Error fetching invoice data for Excel monthly breakdown:', err);
    }
    
    return { customers: [], months: [] };
  };

  const fetchMonthlyCustomerDataFromInvoices = async () => {
    try {
      const dateRange = getDateRange();
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date
        },
        filters: {
          company_code: "045"
        }
      };

      const response = await fetch('https://apisaleeasy.psisat.com/api/report/invoicereport', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status && data.data && data.data.invoices) {
          const monthlyBreakdown = processInvoicesForMonthlyBreakdown(data.data.invoices);
          setMonthlyCustomerData(monthlyBreakdown);
        }
      }
    } catch (err) {
      console.error('Error fetching invoice data for monthly breakdown:', err);
    }
  };

  const processInvoicesForMonthlyBreakdown = (invoices) => {
    console.log(`🔄 Processing ${invoices.length} invoices for monthly breakdown`);
    const customers = {};
    const months = new Set();
    const processedInvoices = new Set(); // Track processed invoices to avoid duplicates

    // Process each invoice
    invoices.forEach((invoice, index) => {
      const customerId = invoice.Cust_No;
      const invoiceDate = new Date(invoice.invoice_date);
      const monthKey = `${invoiceDate.getFullYear()}-${(invoiceDate.getMonth() + 1).toString().padStart(2, '0')}`;
      
      // Create unique identifier for each invoice to prevent duplicates
      const invoiceId = `${customerId}-${invoice.invoice_date}-${invoice.Net_Amount}-${invoice.invoice_no || index}`;
      
      // Skip if this exact invoice was already processed
      if (processedInvoices.has(invoiceId)) {
        console.log(`⚠️  Skipping duplicate invoice: ${invoiceId}`);
        return;
      }
      
      processedInvoices.add(invoiceId);
      
      // Debug first few invoices
      if (index < 3) {
        console.log(`📄 Invoice ${index + 1}:`, {
          customer: customerId,
          date: invoice.invoice_date,
          monthKey: monthKey,
          amount: invoice.Net_Amount,
          invoiceId: invoiceId
        });
      }
      
      months.add(monthKey);
      
      if (!customers[customerId]) {
        customers[customerId] = {
          customer_code: customerId,
          months: {}
        };
      }
      
      if (!customers[customerId].months[monthKey]) {
        customers[customerId].months[monthKey] = {
          total_amount: 0,
          total_bills: 0
        };
      }
      
      customers[customerId].months[monthKey].total_amount += parseFloat(invoice.Net_Amount || 0);
      customers[customerId].months[monthKey].total_bills += 1;
    });

    const result = {
      customers: Object.values(customers),
      months: Array.from(months).sort()
    };

    console.log(`✅ Processed result:`, {
      totalInvoices: invoices.length,
      uniqueInvoices: processedInvoices.size,
      totalCustomers: result.customers.length,
      totalMonths: result.months.length,
      months: result.months,
      sampleCustomer: result.customers[0]
    });

    return result;
  };

  const generateMonthlyDataFromCustomerSummary = (customerData) => {
    console.log('🔍 Analyzing customer data for monthly breakdown...');
    
    if (!customerData || !customerData.customers) {
      console.log('❌ No customer data available');
      return { customers: [], months: [] };
    }

    const customers = [];
    const monthsSet = new Set();
    const processedCustomers = new Set(); // Track processed customers to avoid duplicates

    customerData.customers.forEach(customer => {
      // Skip if customer was already processed
      if (processedCustomers.has(customer.customer_code)) {
        console.log(`⚠️  Skipping duplicate customer: ${customer.customer_code}`);
        return;
      }
      
      processedCustomers.add(customer.customer_code);
      
      console.log(`📊 Processing customer ${customer.customer_code}:`, {
        firstBill: customer.first_bill_date,
        lastBill: customer.last_bill_date,
        totalBills: customer.total_bills,
        totalAmount: customer.total_amount
      });

      // Create a basic monthly entry - we'll assume equal distribution across months
      // This is not perfect but will work until we have proper invoice details
      if (customer.first_bill_date && customer.last_bill_date) {
        const startDate = new Date(customer.first_bill_date);
        const endDate = new Date(customer.last_bill_date);
        
        // Generate months between first and last bill
        const customerMonths = {};
        const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        
        while (current <= endDate) {
          const monthKey = `${current.getFullYear()}-${(current.getMonth() + 1).toString().padStart(2, '0')}`;
          monthsSet.add(monthKey);
          
          // Distribute total amount and bills evenly across months (rough approximation)
          const monthsSpan = Math.max(1, (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1);
          
          customerMonths[monthKey] = {
            total_amount: customer.total_amount / monthsSpan,
            total_bills: Math.ceil(customer.total_bills / monthsSpan)
          };
          
          current.setMonth(current.getMonth() + 1);
        }

        customers.push({
          customer_code: customer.customer_code,
          months: customerMonths
        });
      }
    });

    const result = {
      customers: customers,
      months: Array.from(monthsSet).sort()
    };

    console.log('📈 Generated monthly data:', {
      totalCustomers: result.customers.length,
      uniqueCustomers: processedCustomers.size,
      totalMonths: result.months.length,
      months: result.months,
      sampleCustomer: result.customers[0]
    });

    return result;
  };

  // Pagination calculations
  const totalCustomers = customerData?.customers?.length || 0;
  const totalPages = Math.ceil(totalCustomers / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = customerData?.customers?.slice(startIndex, endIndex) || [];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table when page changes
      document.querySelector('.customer-table')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatCurrencyForExcel = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const fetchCustomerName = async (customerCode, retryCount = 0) => {
    if (customerNames[customerCode] || !customerCode) {
      console.log(`⚡ Customer ${customerCode} already loaded or empty code`);
      return customerNames[customerCode];
    }

    // Special case for cash customer
    if (customerCode === '5890045') {
      setCustomerNames(prev => ({ ...prev, [customerCode]: 'เงินสด' }));
      setCustomerPhones(prev => ({ ...prev, [customerCode]: '-' }));
      return 'เงินสด';
    }

    const maxRetries = 3;
    
    console.log(`🔍 Fetching customer data for: "${customerCode}" (attempt ${retryCount + 1}/${maxRetries})`);
    
    try {
      const requestBody = {
        code: String(customerCode).trim()
      };
      
      console.log(`📤 Sending API request:`, requestBody);
      
      const response = await fetch('https://apisaleeasy.psisat.com/api/technician/by-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log(`📥 API Response status for ${customerCode}: ${response.status}`);

      // Handle 404 and other HTTP errors gracefully
      if (!response.ok) {
        console.warn(`⚠️ API error for customer ${customerCode} (attempt ${retryCount + 1}/${maxRetries}): ${response.status} ${response.statusText}`);
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();
      console.log(`📋 Raw API response for ${customerCode}:`, result);
      
      // Check multiple possible response structures
      if (result && result.status && result.data) {
        console.log(`✅ API Success (structure 1) for ${customerCode}:`, result.data);
        return await processCustomerData(customerCode, result.data);
      } else if (result && result.data && !result.status) {
        console.log(`✅ API Success (structure 2) for ${customerCode}:`, result.data);
        return await processCustomerData(customerCode, result.data);
      } else if (result && Object.keys(result).length > 0 && !result.status && !result.data) {
        console.log(`✅ API Success (structure 3) for ${customerCode}:`, result);
        return await processCustomerData(customerCode, result);
      } else {
        console.warn(`❌ Unexpected API response structure for ${customerCode}:`, result);
        throw new Error(`Invalid response structure`);
      }
    } catch (err) {
      console.error(`Error fetching customer name for ${customerCode} (attempt ${retryCount + 1}/${maxRetries}):`, err);
      
      // Retry if we haven't reached max retries
      if (retryCount < maxRetries - 1) {
        console.log(`🔄 Retrying customer ${customerCode} in 1 second... (attempt ${retryCount + 2}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchCustomerName(customerCode, retryCount + 1);
      }
      
      // After 3 failed attempts, set default values
      console.log(`❌ Failed to load data for customer ${customerCode} after ${maxRetries} attempts, using defaults`);
      return await setCustomerDefaults(customerCode);
    }
  };

  const processCustomerData = async (customerCode, data) => {
    console.log(`🔄 Processing customer data for ${customerCode}:`, data);
    
    // Try multiple field name variations that might be in the API response
    const firstName = data.technician_name || data.name || data.customer_name || data.first_name || '';
    const lastName = data.technician_surname || data.surname || data.last_name || data.customer_surname || '';
    const name = `${firstName}${lastName ? ' ' + lastName : ''}`.trim() || 'ไม่ระบุ';
    
    let phone = data.technician_phone || data.phone || data.customer_phone || data.mobile || '-';
    
    // Format phone number to ensure it's treated as string and preserve leading zeros
    if (phone && phone !== '-') {
      phone = String(phone);
      // If phone number doesn't start with 0 and looks like a Thai mobile number, add 0
      if (/^[89]\d{8}$/.test(phone)) {
        phone = '0' + phone;
      }
    }
    
    // Extract additional customer data with multiple possible field names
    const creditLimit = data.technician_credit_limit || data.credit_limit || data.customer_credit_limit || 0;
    const remainingCredit = data.technician_credit_limit_remaining || data.credit_remaining || data.remaining_credit || 0;
    const district = data.technician_district || data.district || data.customer_district || '-';
    const province = data.technician_province || data.province || data.customer_province || '-';
        
    // Debug: Log what we extracted
    console.log(`📊 Extracted data for ${customerCode}:`, {
      name, phone, creditLimit, remainingCredit, district, province
    });
    
    // Set state with detailed logging
    console.log(`💾 Setting state for customer ${customerCode}...`);
    setCustomerNames(prev => {
      const updated = { ...prev, [customerCode]: name };
      console.log(`📝 Updated customerNames for ${customerCode}: ${name}`);
      return updated;
    });
    setCustomerPhones(prev => {
      const updated = { ...prev, [customerCode]: phone };
      console.log(`📞 Updated customerPhones for ${customerCode}: ${phone}`);
      return updated;
    });
    setCustomerCredits(prev => {
      const updated = { ...prev, [customerCode]: creditLimit };
      console.log(`💳 Updated customerCredits for ${customerCode}: ${creditLimit}`);
      return updated;
    });
    setCustomerRemainingCredits(prev => {
      const updated = { ...prev, [customerCode]: remainingCredit };
      console.log(`💰 Updated customerRemainingCredits for ${customerCode}: ${remainingCredit}`);
      return updated;
    });
    setCustomerDistricts(prev => {
      const updated = { ...prev, [customerCode]: district };
      console.log(`📍 Updated customerDistricts for ${customerCode}: ${district}`);
      return updated;
    });
    setCustomerProvinces(prev => {
      const updated = { ...prev, [customerCode]: province };
      console.log(`🏛️ Updated customerProvinces for ${customerCode}: ${province}`);
      return updated;
    });
    
    console.log(`✅ Successfully processed customer ${customerCode}: ${name}`);
    return name;
  };

  const setCustomerDefaults = async (customerCode) => {
    console.log(`🔧 Setting default values for customer ${customerCode}`);
    setCustomerNames(prev => ({
      ...prev,
      [customerCode]: 'ไม่ระบุ'
    }));
    setCustomerPhones(prev => ({
      ...prev,
      [customerCode]: '-'
    }));
    setCustomerCredits(prev => ({
      ...prev,
      [customerCode]: 0
    }));
    setCustomerRemainingCredits(prev => ({
      ...prev,
      [customerCode]: 0
    }));
    setCustomerDistricts(prev => ({
      ...prev,
      [customerCode]: '-'
    }));
    setCustomerProvinces(prev => ({
      ...prev,
      [customerCode]: '-'
    }));
    return 'ไม่ระบุ';
  };

  const fetchAllCustomerNames = async (customers) => {
    if (!customers || customers.length === 0) return;
    
    console.log(`🚀 Starting to load data for ${customers.length} customers...`);
    setLoadingNames(true);
    setCustomerDataReady(false); // Mark as not ready while loading
    
    try {
      const uniqueCustomerCodes = [...new Set(customers.map(c => c.customer_code))];
      console.log(`📋 Unique customers to load: ${uniqueCustomerCodes.length}`);
      
      // Load all customers in parallel for speed, then check completeness
      console.log(`🚀 Starting parallel loading of all ${uniqueCustomerCodes.length} customers...`);
      
      let attempt = 1;
      const maxAttempts = 3;
      let allComplete = false;
      
      while (attempt <= maxAttempts && !allComplete) {
        console.log(`📋 Loading attempt ${attempt}/${maxAttempts}`);
        
        // Filter customers that still need loading
        const customersToLoad = uniqueCustomerCodes.filter(code => !customerNames[code]);
        console.log(`🔍 Customers still need loading: ${customersToLoad.length}/${uniqueCustomerCodes.length}`);
        
        if (customersToLoad.length === 0) {
          console.log(`✅ All customers already loaded!`);
          allComplete = true;
          break;
        }
        
        // Load all remaining customers in parallel
        console.log(`⚡ Loading ${customersToLoad.length} customers in parallel...`);
        const promises = customersToLoad.map(async (code) => {
          try {
            console.log(`🔍 Starting load for: ${code}`);
            const result = await fetchCustomerName(code);
            console.log(`✅ Completed ${code}: ${result}`);
            return { code, status: 'fulfilled', value: result };
          } catch (error) {
            console.error(`❌ Failed ${code}:`, error);
            return { code, status: 'rejected', reason: error };
          }
        });
        
        const results = await Promise.allSettled(promises);
        
        // Log results
        const fulfilled = results.filter(r => r.status === 'fulfilled').length;
        const rejected = results.filter(r => r.status === 'rejected').length;
        console.log(`📊 Attempt ${attempt} results: ${fulfilled} success, ${rejected} failed`);
        
        // Check if we got everything
        const stillMissing = uniqueCustomerCodes.filter(code => !customerNames[code]);
        console.log(`🔍 Still missing after attempt ${attempt}: ${stillMissing.length} customers`);
        
        if (stillMissing.length === 0) {
          console.log(`🎉 All customers loaded successfully on attempt ${attempt}!`);
          allComplete = true;
        } else if (attempt < maxAttempts) {
          console.log(`⏳ Waiting 3 seconds before retry attempt ${attempt + 1}...`);
          console.log(`🔄 Will retry these customers: [${stillMissing.join(', ')}]`);
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
        
        attempt++;
      }
      
      // Final check
      const finalMissing = uniqueCustomerCodes.filter(code => !customerNames[code]);
      if (finalMissing.length > 0) {
        console.warn(`⚠️ Could not load ${finalMissing.length} customers after ${maxAttempts} attempts:`);
        console.warn(`   Missing: [${finalMissing.join(', ')}]`);
      } else {
        console.log(`🎉 Successfully loaded ALL ${uniqueCustomerCodes.length} customers!`);
      }
      
      // Generate final summary
      const totalCustomers = uniqueCustomerCodes.length;
      const loadedCustomers = uniqueCustomerCodes.filter(code => customerNames[code]).length;
      const missingCustomers = totalCustomers - loadedCustomers;
      
      console.log(`🎉 Finished loading customer data:`);
      console.log(`   📝 Total customers: ${totalCustomers}`);
      console.log(`   ✅ Successfully loaded: ${loadedCustomers}`);
      console.log(`   ❌ Failed to load: ${missingCustomers}`);
      console.log(`   📈 Success rate: ${Math.round((loadedCustomers / totalCustomers) * 100)}%`);
      
      // Show current state of customerNames
      console.log(`🗂️ Current customerNames state:`, Object.keys(customerNames).length, 'entries');
      console.log(`📋 customerNames keys:`, Object.keys(customerNames));
      
      // Wait a bit more for state updates to propagate
      console.log(`⏳ Waiting for state updates to complete...`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
    } catch (err) {
      console.error('Error fetching customer names:', err);
    } finally {
      setLoadingNames(false);
      setCustomerDataReady(true); // Mark as ready when loading is complete
      console.log(`🏁 Customer data loading process completed - Export button now available`);
    }
  };

  const exportToExcel = async () => {
    if (!customerData || !customerData.customers) {
      alert('ไม่มีข้อมูลสำหรับ export');
      return;
    }

    setExportLoading(true);

    try {
      console.log('📊 Using existing customer data for export...');
      
      // Simply proceed with whatever customer data we have
      // No need to re-load since customerDataReady ensures data is complete
      const allCustomerCodes = customerData.customers.map(c => c.customer_code);
      const availableNames = Object.keys(customerNames).length;
      
      console.log(`📋 Export data summary:`);
      console.log(`   Total customers: ${allCustomerCodes.length}`);
      console.log(`   Available names: ${availableNames}`);
      console.log(`   Data completeness: ${Math.round((availableNames / allCustomerCodes.length) * 100)}%`);

      // Use existing customer data to generate monthly breakdown
      console.log('Generating monthly data from existing customer data...');
      
      // Check if customerData has detailed invoice information
      console.log('Customer data structure:', customerData);
      
      // Try to create monthly data from available information
      const freshMonthlyData = generateMonthlyDataFromCustomerSummary(customerData);

      // First sheet - Customer Summary
      const summaryData = (customerData.customers || []).map((customer) => ({
        'อันดับ': customer.rank,
        'รหัสลูกค้า': customer.customer_code,
        'ชื่อลูกค้า': customerNames[customer.customer_code] || 'ไม่ระบุ',
        'เบอร์โทร': customerPhones[customer.customer_code] || '-',
        'วงเงินเครดิต': formatCurrencyForExcel(customerCredits[customer.customer_code] || 0),
        'เครดิตคงเหลือ': formatCurrencyForExcel(customerRemainingCredits[customer.customer_code] || 0),
        'อำเภอ': customerDistricts[customer.customer_code] || '-',
        'จังหวัด': customerProvinces[customer.customer_code] || '-',
        'จำนวนบิล': customer.total_bills?.toLocaleString() || '0',
        'ยอดขายรวม': formatCurrencyForExcel(customer.total_amount),
        'วันที่แรก': formatDate(customer.first_bill_date),
        'วันที่สุดท้าย': formatDate(customer.last_bill_date)
      }));

      // Second sheet - Monthly Customer Breakdown
      const monthlyData = generateMonthlyBreakdownData(freshMonthlyData);

      // Create workbook with multiple sheets
      const workbook = createExcelWorkbook([
        { name: 'อันดับลูกค้า', data: summaryData },
        { name: 'ยอดขายรายเดือน', data: monthlyData }
      ]);

      // Download the Excel file
      downloadExcelFile(workbook, `อันดับลูกค้า_${startDate}_ถึง_${endDate}_ทั้งหมด_${totalCustomers}_คน.xlsx`);

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('เกิดข้อผิดพลาดในการ Export ข้อมูล');
    } finally {
      setExportLoading(false);
    }
  };



  const generateMonthlyBreakdownData = (monthlyData = null) => {
    if (!customerData || !customerData.customers) {
      return [];
    }

    // Use provided monthly data or fall back to state
    const monthlyDataToUse = monthlyData || monthlyCustomerData;

    // Get all customers from main customer data
    const allCustomers = customerData.customers || [];
    
    // Create monthly data map from API data if available
    const monthlyDataMap = {};
    const allMonthsWithData = new Set();
    
    if (monthlyDataToUse && monthlyDataToUse.customers) {
      monthlyDataToUse.customers.forEach(customer => {
        const customerMonths = customer.months || {};
        monthlyDataMap[customer.customer_code] = customerMonths;
        
        // Collect all months that have actual data (avoiding duplicates)
        Object.keys(customerMonths).forEach(month => {
          if (customerMonths[month] && customerMonths[month].total_amount > 0) {
            allMonthsWithData.add(month);
          }
        });
      });
    }
    
    // Include all months with data plus any in the selected range
    const rangeMonths = generateMonthsInRange(startDate, endDate);
    const allMonths = [...new Set([...rangeMonths, ...Array.from(allMonthsWithData)])].sort();
    
    console.log(`Found ${allMonths.length} months to display:`, allMonths);
    console.log('Monthly data structure:', monthlyDataToUse);
    console.log('Monthly data map:', monthlyDataMap);
    console.log('Sample customer monthly data:', Object.values(monthlyDataMap)[0]);

    // Create data rows for all customers (without header row)
    const dataRows = allCustomers.map(customer => {
      const row = {
        'รหัสลูกค้า': customer.customer_code,
        'ชื่อลูกค้า': customerNames[customer.customer_code] || 'ไม่ระบุ',
        'เบอร์โทร': customerPhones[customer.customer_code] || '-'
      };

      // Add monthly data
      allMonths.forEach(month => {
        const customerMonthlyData = monthlyDataMap[customer.customer_code];
        const monthData = customerMonthlyData && customerMonthlyData[month];
        
        // Debug for first customer
        if (customer.customer_code === allCustomers[0]?.customer_code) {
          console.log(`Customer ${customer.customer_code} - Month ${month}:`, {
            customerMonthlyData,
            monthData,
            hasAmount: monthData?.total_amount > 0
          });
        }
        
        if (monthData && monthData.total_amount > 0) {
          row[formatMonthName(month)] = 'มี';
        } else {
          row[formatMonthName(month)] = 'ไม่มี';
        }
      });

      return row;
    });

    return dataRows;
  };

  const generateMonthsInRange = (startDate, endDate) => {
    const months = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const current = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (current <= end) {
      const year = current.getFullYear();
      const month = (current.getMonth() + 1).toString().padStart(2, '0');
      months.push(`${year}-${month}`);
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  const formatMonthName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const monthNames = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const createExcelWorkbook = (sheets) => {
    // Create a new workbook using XLSX
    const workbook = XLSX.utils.book_new();

    sheets.forEach((sheet) => {
      if (sheet.data.length === 0) return;
      
      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(sheet.data);
      
      // Add the sheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
    });

    return workbook;
  };

  const downloadExcelFile = (workbook, filename) => {
    // Generate Excel file using XLSX
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    
    // Create blob and download
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the object URL
    URL.revokeObjectURL(url);
  };


  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-600 bg-yellow-100';
    if (rank === 2) return 'text-gray-600 bg-gray-100';
    if (rank === 3) return 'text-orange-600 bg-orange-100';
    if (rank <= 10) return 'text-blue-600 bg-blue-100';
    return 'text-gray-500 bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">อันดับลูกค้าที่มียอดซื้อขาย</h1>
              <p className="text-gray-600">ดูสรุปยอดขายและอันดับลูกค้ารายเดือน</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">เลือกข้อมูลที่ต้องการดู</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-blue-800 text-sm">
                <strong>หมายเหตุ:</strong> สามารถเลือกข้อมูลได้ตั้งแต่วันที่ 1 พฤศจิกายน 2025 จนถึงวันปัจจุบัน
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">วันที่เริ่มต้น</label>
                <MaskedDatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholderText="DD/MM/YYYY"
                  minDate={new Date(getMinAllowedDate() + 'T00:00:00')}
                  maxDate={new Date(getMaxAllowedDate() + 'T00:00:00')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">วันที่สิ้นสุด</label>
                <MaskedDatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholderText="DD/MM/YYYY"
                  minDate={new Date(getMinAllowedDate() + 'T00:00:00')}
                  maxDate={new Date(getMaxAllowedDate() + 'T00:00:00')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-end gap-2">
                <button
                  onClick={fetchCustomerAnalytics}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
                >
                  {loading ? 'กำลังโหลด...' : 'ดูข้อมูล'}
                </button>
                <button
                  onClick={exportToExcel}
                  disabled={!customerData || !customerData.customers || customerData.customers.length === 0 || exportLoading || !customerDataReady}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                  title={!customerDataReady ? "กำลังโหลดข้อมูลลูกค้า กรุณารอสักครู่..." : "Export ไป Excel (1 ไฟล์ 2 Sheets: อันดับลูกค้า และ ยอดขายรายเดือน)"}
                >
                  {exportLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      กำลัง Export...
                    </>
                  ) : !customerDataReady ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      โหลดข้อมูล...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Export Excel
                    </>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
                {error}
              </div>
            )}

            {loadingNames && !customerDataReady && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-600">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <div>
                    <p className="font-medium">กำลังโหลดข้อมูลลูกค้า...</p>
                    <p className="text-sm">กรุณารอจนกว่าข้อมูลลูกค้าจะโหลดครบถ้วน ปุ่ม Export Excel จะใช้งานได้เมื่อเสร็จแล้ว</p>
                  </div>
                </div>
              </div>
            )}

            {exportLoading && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-600">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                  <div>
                    <p className="font-medium">กำลังเตรียมไฟล์ Excel...</p>
                    <p className="text-sm">กำลังสร้างไฟล์ Excel พร้อมข้อมูลทั้ง 2 sheets</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          {customerData && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">ลูกค้าทั้งหมด</h3>
                      <p className="text-2xl font-bold text-gray-900">{customerData.summary?.total_customers?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">บิลทั้งหมด</h3>
                      <p className="text-2xl font-bold text-gray-900">{customerData.summary?.total_bills?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">ยอดขายรวม</h3>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(customerData.summary?.total_amount)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">ยอดขายเฉลี่ยต่อลูกค้า</h3>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(customerData.summary?.average_amount_per_customer)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Period Display */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">ช่วงเวลา</h3>
                  <p className="text-gray-600">
                    {formatDate(customerData.period?.start_date)} - {formatDate(customerData.period?.end_date)}
                  </p>
                </div>
              </div>

              {/* Customer List */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 customer-table">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                    <h3 className="text-xl font-semibold text-gray-900">อันดับลูกค้า (ทั้งหมด {totalCustomers} คน)</h3>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">แสดง:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                        className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                      </select>
                      <span className="text-sm text-gray-600">รายการ</span>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">อันดับ</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">รหัสลูกค้า</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">ชื่อลูกค้า</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">เบอร์โทร</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-900">จำนวนบิล</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-900">ยอดขายรวม</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-900">เปอร์เซ็นต์ยอดขาย</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-900">วันที่แรก</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-900">วันที่สุดท้าย</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCustomers.map((customer) => (
                        <tr key={customer.customer_code} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold ${getRankColor(customer.rank)}`}>
                              {customer.rank}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-900">{customer.customer_code}</td>
                          <td className="py-3 px-4 text-gray-900">
                            {loadingNames ? (
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-gray-500">โหลด...</span>
                              </div>
                            ) : (
                              customerNames[customer.customer_code] || 'ไม่ระบุ'
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {loadingNames ? (
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-gray-500">โหลด...</span>
                              </div>
                            ) : (
                              customerPhones[customer.customer_code] || '-'
                            )}
                          </td>
                          <td className="py-3 px-4 text-right text-gray-900">{customer.total_bills?.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">{formatCurrency(customer.total_amount)}</td>
                          <td className="py-3 px-4 text-right text-gray-600">{customer.percentage_of_total_amount}%</td>
                          <td className="py-3 px-4 text-center text-gray-600 text-sm">
                            {formatDate(customer.first_bill_date)}
                          </td>
                          <td className="py-3 px-4 text-center text-gray-600 text-sm">
                            {formatDate(customer.last_bill_date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        แสดง {startIndex + 1}-{Math.min(endIndex, totalCustomers)} จากทั้งหมด {totalCustomers} ลูกค้า
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Previous Button */}
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          ก่อนหน้า
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                          {(() => {
                            const pages = [];
                            const maxVisiblePages = 7;
                            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                            
                            if (endPage - startPage + 1 < maxVisiblePages) {
                              startPage = Math.max(1, endPage - maxVisiblePages + 1);
                            }

                            // First page
                            if (startPage > 1) {
                              pages.push(
                                <button
                                  key={1}
                                  onClick={() => handlePageChange(1)}
                                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                                >
                                  1
                                </button>
                              );
                              
                              if (startPage > 2) {
                                pages.push(
                                  <span key="ellipsis1" className="px-2 py-2 text-gray-400">...</span>
                                );
                              }
                            }

                            // Visible pages
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <button
                                  key={i}
                                  onClick={() => handlePageChange(i)}
                                  className={`px-3 py-2 border rounded-lg text-sm font-medium ${
                                    currentPage === i
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                  }`}
                                >
                                  {i}
                                </button>
                              );
                            }

                            // Last page
                            if (endPage < totalPages) {
                              if (endPage < totalPages - 1) {
                                pages.push(
                                  <span key="ellipsis2" className="px-2 py-2 text-gray-400">...</span>
                                );
                              }
                              
                              pages.push(
                                <button
                                  key={totalPages}
                                  onClick={() => handlePageChange(totalPages)}
                                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                                >
                                  {totalPages}
                                </button>
                              );
                            }

                            return pages;
                          })()}
                        </div>

                        {/* Next Button */}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          ถัดไป
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerSummaryComponent;